import React from "react";

import { subscriptionsApi } from "../api/subscriptionsApi";
import { useAuth } from "../auth/authStore";
import SubscriptionLimitModal from "../components/subscription/SubscriptionLimitModal";
import {
  extractTotal,
  getFeatureLimit,
  getFeatureMeta,
  isUnlimited,
  nextPlanCode,
  normalizePlanCode,
  planIndex,
} from "./subscriptionLimits";

function resolvePlanNameByCode(plans = [], code = "") {
  const token = normalizePlanCode(code);
  const matched = (Array.isArray(plans) ? plans : []).find(
    (plan) => normalizePlanCode(plan?.code) === token
  );
  return String(matched?.name || "").trim() || token;
}

export function useSubscriptionLimitsGate() {
  const { isAuthenticated } = useAuth();
  const [subscriptionState, setSubscriptionState] = React.useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = React.useState([]);
  const [limitModal, setLimitModal] = React.useState({
    open: false,
    title: "",
    action: "",
    used: 0,
    limit: 0,
    nextPlanName: "",
  });

  const loadSubscription = React.useCallback(async () => {
    if (!isAuthenticated) {
      setSubscriptionState(null);
      setSubscriptionPlans([]);
      return;
    }
    try {
      const plans = await subscriptionsApi.listPlans();
      const state = await subscriptionsApi.getMySubscription({ plans });
      setSubscriptionPlans(Array.isArray(plans) ? plans : []);
      setSubscriptionState(state && typeof state === "object" ? state : null);
    } catch {
      setSubscriptionPlans([]);
      setSubscriptionState(null);
    }
  }, [isAuthenticated]);

  React.useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  const currentPlanCode = React.useMemo(
    () => normalizePlanCode(subscriptionState?.planCode || "FREE"),
    [subscriptionState?.planCode]
  );

  const openLimitModal = React.useCallback(
    ({ featureKey, used, limit }) => {
      const meta = getFeatureMeta(featureKey);
      const nextCode = nextPlanCode(currentPlanCode);
      const hasHigherPlan = planIndex(nextCode) > planIndex(currentPlanCode);
      const nextPlanName = hasHigherPlan
        ? resolvePlanNameByCode(subscriptionPlans, nextCode)
        : "максимальный тариф";

      setLimitModal({
        open: true,
        title: meta.title,
        action: meta.action,
        used: Number.isFinite(Number(used)) ? Number(used) : 0,
        limit: Number.isFinite(Number(limit)) ? Number(limit) : 0,
        nextPlanName,
      });
    },
    [currentPlanCode, subscriptionPlans]
  );

  const ensureWithinLimit = React.useCallback(
    ({ featureKey, usage = 0 }) => {
      const limit = getFeatureLimit(currentPlanCode, featureKey);
      if (isUnlimited(limit)) return true;
      const preparedUsage = extractTotal({ totalElements: Number(usage) || 0 }, 0);
      if (preparedUsage < limit) return true;
      openLimitModal({ featureKey, used: preparedUsage, limit });
      return false;
    },
    [currentPlanCode, openLimitModal]
  );

  const closeLimitModal = React.useCallback(() => {
    setLimitModal((prev) => ({ ...prev, open: false }));
  }, []);

  return {
    currentPlanCode,
    ensureWithinLimit,
    refreshSubscriptionLimits: loadSubscription,
    limitModal: (
      <SubscriptionLimitModal
        open={limitModal.open}
        title={limitModal.title}
        action={limitModal.action}
        used={limitModal.used}
        limit={limitModal.limit}
        nextPlanName={limitModal.nextPlanName}
        onClose={closeLimitModal}
      />
    ),
  };
}
